/**
 * The level's lights: one hanging inside each room, plus the single shadow-casting sun.
 *
 * This lives here rather than in `GameCanvas` because both need the built level — the
 * room lights need to know where the rooms are, and the sun's shadow camera is fitted
 * to the house's actual footprint instead of a fixed box. `GameCanvas` keeps only the
 * hemisphere fill, which needs no level at all.
 *
 * The room lights cast, and they are spotlights so they can afford to. Leaving them
 * shadowless made the house argue with itself — a box standing under a lit fitting was
 * shadowed by the sun, on the far side of a solid wall. A *point* light casting would
 * mean six shadow-map faces each; a downward spotlight needs one, and a ceiling fitting
 * only ever throws light down into its own room anyway.
 *
 * This takes the scene from one shadow caster to one per room, which is the single
 * biggest deviation from the mobile budget in `GameCanvas` — see the change's design
 * doc, Complexity Tracking, for the justification and the fallback if it fails to hold.
 *
 * An outdoor level spends none of that. It has no rooms, so it has no lamps and no windows:
 * the sun alone, on a map fitted to grounds that are three times the span of a house. See
 * `hangingLights` below for why that needs saying out loud rather than falling out.
 */

import { useState, type ReactNode } from 'react';
import { Object3D } from 'three';
import type { BuiltLevel } from '../game/profile/buildGeometry';
import { buildLighting, sunPosition } from '../game/profile/buildLighting';
import type { LevelSpec } from '../game/profile/types';
import { useGameStore } from '../store/gameStore';

type Props = {
  built: BuiltLevel;
  /** Needed as well as the built geometry: rooms may author their own lighting. */
  level: LevelSpec;
};

/**
 * Shadow map texels per metre of frustum. At a 512 map this is what decides whether a
 * box's shadow has visible stair-stepping: the old fixed 24 m frustum gave 21 px/m,
 * which is why edges looked chewed.
 */
const SHADOW_MAP_SIZE = 1024;

/**
 * The sun's map on a grounds level, where it is the *only* shadow caster in the scene.
 *
 * A house is a couple of dozen metres across and shares its budget with a lamp in every
 * room — nineteen shadow renders a frame between them. Maze grounds have no rooms and so
 * no lamps: one render, and a frustum three times as wide to cover. At 1024 that is about
 * twenty texels a metre and every hedge edge is visibly chewed. Four times the map on a
 * twentieth of the shadow work is still far inside what the house already spends.
 */
const GROUNDS_SHADOW_MAP_SIZE = 2048;

/**
 * One face of a room lamp's cube shadow map. A point light casts in every direction, so
 * three.js renders **six** of these per lamp per frame — the reason this is 512 where the
 * sun gets 1024. A bulb hanging in the middle of a room is only ever a few metres from
 * anything it lights, so each face covers little ground and 512 is ample.
 *
 * This was a spotlight first, precisely because a cone needs one shadow render instead of
 * six. It was rejected on looks: a cone lights a circle of floor and leaves the ceiling
 * and the top of every wall dark, with a visible curved edge partway up. Real ceiling
 * bulbs light in all directions, and the room read as though it were under a searchlight.
 */
const ROOM_SHADOW_FACE = 512;

/**
 * Aims a light at a point, the only way three.js actually supports.
 *
 * A light points from its position towards `light.target`, and three.js is explicit that
 * "for the target's position to be changed to anything other than the default, it must be
 * added to the scene" — the default target is a bare `Object3D` outside the scene graph,
 * so its world matrix is never updated and setting `target-position` on it silently does
 * nothing. Every light then aims at world origin instead.
 *
 * That is not a cosmetic mistake: a spotlight aimed sideways lights the wrong wall and,
 * once architecture casts shadows, throws the room into darkness from behind its own
 * walls. So the target is a real object, rendered into the scene, and the light is
 * pointed at it.
 */
function Aimed({
  at,
  children,
}: {
  at: [number, number, number];
  children: (target: Object3D) => ReactNode;
}) {
  const [target] = useState(() => new Object3D());

  return (
    <>
      <primitive object={target} position={at} />
      {children(target)}
    </>
  );
}

export function LevelLighting({ built, level }: Props) {
  const roomLights = useGameStore((store) => store.roomLights);
  const { lights, windowLights, shadow } = buildLighting(built, level);

  /**
   * Outdoors there are no rooms, so there is nothing to hang a lamp in and no window for
   * daylight to come through — one shadow render a frame instead of nineteen (design D5).
   *
   * The filter is not belt and braces. `buildLighting` derives a light per *floor*, not per
   * room, and a grounds level does have one floor: the grass. Left alone it would hang a
   * shadow-casting bulb at ground level in the middle of the maze — six cube faces, lighting
   * the hedges from inside their own footings. (`buildFixtures` already declines to give it
   * a switch or a cord, because it looks the room up and there is none.) Nothing to fix in
   * `src/game/`: a floor is genuinely all the pure layer knows, and which floors deserve a
   * lamp is a question about lighting a *room*, which is this file's business.
   */
  const isGrounds = built.grounds !== null;
  const hangingLights = isGrounds ? [] : lights;
  const daylight = isGrounds ? [] : windowLights;

  // The sun sits off one corner of the house and aims at its middle, so the shadow
  // frustum below is centred on something it actually contains. Derived rather than
  // written out here because the moon stands along the same line (design D9) and the two
  // must not be able to drift apart. On grounds the same function stands it far steeper —
  // near-overhead moonlight — so hedge shadows stay at the hedges' own feet.
  const at = sunPosition(built);
  const sun: [number, number, number] = [at.x, at.y, at.z];

  return (
    <>
      {hangingLights.map((light) => {
        const lit = (roomLights[light.roomId] ?? 'on') === 'on';
        return (
          <pointLight
            key={`light-${light.roomId}`}
            castShadow
            position={[light.position.x, light.position.y, light.position.z]}
            color={light.color}
            // Switched off means zero intensity, never unmounted. Changing the *number*
            // of lights in a scene makes three.js recompile every material, which is a
            // visible hitch; changing one uniform is free.
            intensity={lit ? light.intensity : 0}
            distance={light.distance}
            decay={1.4}
            // Per cube face, not in total — a point light's shadow is six of these.
            shadow-mapSize-width={ROOM_SHADOW_FACE}
            shadow-mapSize-height={ROOM_SHADOW_FACE}
            shadow-camera-near={0.3}
            shadow-bias={-0.0006}
            shadow-normalBias={0.05}
          />
        );
      })}

      {daylight.map((light) => (
        <pointLight
          key={`window-light-${light.windowId}`}
          position={[light.position.x, light.position.y, light.position.z]}
          color={light.color}
          intensity={light.intensity}
          distance={light.distance}
          decay={1.6}
        />
      ))}

      <Aimed at={[shadow.center.x, 0, shadow.center.z]}>
        {(target) => (
          <directionalLight
            castShadow
            target={target}
            position={sun}
            // Dim and cold: it is raining out there. Indoors this no longer lights the
            // interior — that is the room lamps' job — it only keeps the house from being
            // pitch black where they do not reach, and grounds props with a soft shadow.
            // Outdoors it is the only light the level has beyond the hemisphere fill, so
            // it carries the weight of every lamp it replaced: overcast daylight, still
            // cold, bright enough that a hedge reads as green rather than black.
            intensity={isGrounds ? 1.35 : 0.35}
            color="#9aabc4"
            shadow-mapSize-width={isGrounds ? GROUNDS_SHADOW_MAP_SIZE : SHADOW_MAP_SIZE}
            shadow-mapSize-height={isGrounds ? GROUNDS_SHADOW_MAP_SIZE : SHADOW_MAP_SIZE}
            shadow-camera-left={-shadow.radius}
            shadow-camera-right={shadow.radius}
            shadow-camera-top={shadow.radius}
            shadow-camera-bottom={-shadow.radius}
            shadow-camera-near={0.5}
            shadow-camera-far={shadow.radius * 4 + 20}
            // Kills the acne a tighter, sharper map would otherwise show on the floor.
            shadow-bias={-0.0008}
            shadow-normalBias={0.02}
          />
        )}
      </Aimed>
    </>
  );
}
